// components/SurveyResponseDashboard.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChartBarIcon, 
  UsersIcon, 
  DocumentTextIcon,
  CalendarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/solid';
import { surveyResponseService } from '../../services/surveyResponseService';
import { surveysAPI } from '../../services/api';

const SurveyResponseDashboard = () => {
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSurveys();
  }, []);

  useEffect(() => {
    if (selectedSurveyId) {
      loadDashboardData();
    }
  }, [selectedSurveyId]);

  const loadSurveys = async () => {
    try {
      setSurveysLoading(true);
      const response = await surveysAPI.getAll();
      setSurveys(response.data);
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError('Failed to load surveys');
    } finally {
      setSurveysLoading(false);
    }
  };

  const loadDashboardData = async (employeeIds = []) => {
    if (!selectedSurveyId) return;
    
    try {
      setLoading(true);
      let data;
      
      if (employeeIds.length > 0) {
        data = await surveyResponseService.getFilteredSurveyResponses(selectedSurveyId, employeeIds);
      } else {
        data = await surveyResponseService.getSurveyResponseDashboard(selectedSurveyId);
      }
      
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load survey response data');
      console.error('Error loading survey responses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyChange = (surveyId) => {
    setSelectedSurveyId(surveyId);
    setSelectedEmployees([]);
    setSelectAll(false);
    setDashboardData(null);
  };

  const handleEmployeeSelection = (employeeId) => {
    const newSelectedEmployees = selectedEmployees.includes(employeeId)
      ? selectedEmployees.filter(id => id !== employeeId)
      : [...selectedEmployees, employeeId];
    
    setSelectedEmployees(newSelectedEmployees);
    loadDashboardData(newSelectedEmployees);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
      setSelectAll(false);
      loadDashboardData([]);
    } else {
      const allEmployeeIds = dashboardData?.employees?.map(emp => emp.id) || [];
      setSelectedEmployees(allEmployeeIds);
      setSelectAll(true);
      loadDashboardData(allEmployeeIds);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#10B981'; // Green
    if (rating >= 3) return '#F59E0B'; // Yellow
    if (rating >= 2) return '#EF4444'; // Red
    return '#6B7280'; // Gray
  };

  if (surveysLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section with Survey Selection */}
      <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-orange-50 via-white to-orange-50 p-6 rounded-2xl shadow-md">
        {/* Left Panel - Survey Selection */}
        <div className="w-full lg:w-1/4 bg-gradient-to-br from-orange-100 via-white to-orange-100 rounded-2xl shadow-md p-6 border border-orange-100 h-fit">
          <div className="w-48 h-48 bg-gradient-to-br from-orange-200 to-orange-300 rounded-2xl flex items-center justify-center mb-3 mx-auto">
            <ChartBarIcon className="w-16 h-16 text-orange-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">Response Analytics</h2>
          <p className="text-gray-500 text-sm mt-1 leading-snug">
            View and analyze survey responses with detailed insights and filtering options.
          </p>
        </div>

        {/* Right Panel - Survey Selection Form */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex-1 bg-gradient-to-br from-orange-50 via-white to-orange-50 rounded-xl shadow-sm p-6 border border-orange-100"
        >
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Survey *
            </label>
            <select
              value={selectedSurveyId}
              onChange={(e) => handleSurveyChange(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none shadow-sm bg-white text-gray-800"
            >
              <option value="">Choose a survey to analyze</option>
              {surveys.map(survey => (
                <option key={survey.id} value={survey.id}>
                  {survey.name}
                </option>
              ))}
            </select>
            <p className="text-gray-500 text-xs mt-1">
              Select a survey to view response analytics and insights
            </p>
          </div>

          {selectedSurveyId && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 text-orange-700">
                <CheckCircleIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Survey selected</span>
              </div>
              <p className="text-orange-600 text-xs mt-1">
                Ready to analyze responses. Use the filters below to refine your view.
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && selectedSurveyId && (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          <span className="ml-3 text-gray-600">Loading response data...</span>
        </div>
      )}

      {/* Dashboard Content */}
      {dashboardData && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Survey Header Section */}
          <div className="bg-white shadow rounded-2xl p-6 border border-gray-200">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900">
                  {dashboardData.survey.name}
                </h1>
                <p className="mt-1 text-gray-600 text-sm">{dashboardData.survey.description}</p>
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span>Period: {formatDate(dashboardData.survey.startDate)} - {formatDate(dashboardData.survey.endDate)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <DocumentTextIcon className="w-4 h-4" />
                    <span>Created: {formatDate(dashboardData.survey.createdDate)}</span>
                  </div>
                </div>
              </div>
              <div className="text-center lg:text-right">
                <div 
                  className="text-3xl font-bold"
                  style={{ color: getRatingColor(dashboardData.overallAverageRating) }}
                >
                  {dashboardData.overallAverageRating.toFixed(1)}
                </div>
                <div className="text-sm text-gray-500">
                  Overall Rating
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UsersIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-blue-900 truncate">
                      Total Responses
                    </dt>
                    <dd className="text-2xl font-semibold text-blue-900">
                      {dashboardData.totalResponses}
                    </dd>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UsersIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-green-900 truncate">
                      Total Employees
                    </dt>
                    <dd className="text-2xl font-semibold text-green-900">
                      {dashboardData.totalEmployees}
                    </dd>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-purple-900 truncate">
                      Response Rate
                    </dt>
                    <dd className="text-2xl font-semibold text-purple-900">
                      {((dashboardData.totalResponses / dashboardData.totalEmployees) * 100).toFixed(1)}%
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Employee Filter Section */}
          <div className="bg-white shadow rounded-2xl p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filter by Employees</h3>
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 text-sm font-medium text-orange-600 hover:text-orange-500 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
              >
                {selectAll ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                {dashboardData.employees.map((employee) => (
                  <div key={employee.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      id={`employee-${employee.id}`}
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={() => handleEmployeeSelection(employee.id)}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`employee-${employee.id}`}
                      className="ml-3 text-sm text-gray-700 cursor-pointer"
                    >
                      {employee.displayName}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Category-wise Responses */}
          <div className="space-y-4">
            {dashboardData.categorySummaries.map((category, index) => (
              <motion.div
                key={category.categoryId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white shadow rounded-2xl overflow-hidden border border-gray-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {category.categoryName}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">Category Average:</span>
                      <span 
                        className="text-xl font-bold px-3 py-1 rounded-full"
                        style={{ 
                          color: getRatingColor(category.averageRating),
                          backgroundColor: `${getRatingColor(category.averageRating)}15`
                        }}
                      >
                        {category.averageRating.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {category.questions.map((question) => (
                      <div key={question.questionId} className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-3 border-b border-gray-100 last:border-b-0 gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800">{question.questionText}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {question.totalResponses} responses
                          </p>
                        </div>
                        <div className="flex items-center space-x-4 min-w-[200px]">
                          <div 
                            className="w-16 text-right font-semibold text-lg px-3 py-1 rounded"
                            style={{ 
                              color: getRatingColor(question.averageRating),
                              backgroundColor: `${getRatingColor(question.averageRating)}15`
                            }}
                          >
                            {question.averageRating}
                          </div>
                          <div 
                            className="w-24 h-3 bg-gray-200 rounded-full overflow-hidden flex-shrink-0"
                            title={`Rating: ${question.averageRating}`}
                          >
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${(question.averageRating / 5) * 100}%`,
                                backgroundColor: getRatingColor(question.averageRating)
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* No Survey Selected State */}
      {!selectedSurveyId && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Survey Selected</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Please select a survey from the dropdown above to view response analytics and insights.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default SurveyResponseDashboard;